import React, { useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper';
import 'swiper/swiper-bundle.min.css';

import { Typo, Button, Flex, Loading, Avatar, Figure } from '../../ui';
import theme from '../../ui/theme';
import { getUserThemeColor, getDarkerThemeColor, getUserThemeContrastColor, toRgba, shadeColor, themeGradientBg } from '../../ui/theme/colors';
import { media, CONTENT_STANDARD, CONTENT_MINI } from '../../utils/media';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const giftCardOpen = keyframes`
  0% {
    transform: rotateX(-90deg) scale(0.8);
    opacity: 0;
  }
  50% {
    transform: rotateX(-20deg) scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: rotateX(0) scale(1);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 ${toRgba(theme.colors.NEU_0, 0.4)};
  }
  70% {
    box-shadow: 0 0 0 15px ${toRgba(theme.colors.NEU_0, 0)};
  }
  100% {
    box-shadow: 0 0 0 0 ${toRgba(theme.colors.NEU_0, 0)};
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// ============================================
// HERO SECTION STYLES
// ============================================

const HeroWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  background: ${({ $themeColor }) => themeGradientBg($themeColor)};

  ${media.lessThan('tablet')} {
    min-height: auto;
  }
`;

const LogoSection = styled.section`
  background: ${theme.colors.NEU_0};
  padding: 40px 24px 30px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.8s ease-out;
  z-index: 2;
  clip-path: ellipse(85% 100% at 50% 0%);

  ${media.lessThan('tablet')} {
    padding: 30px 20px 60px;
    clip-path: ellipse(95% 100% at 50% 0%);
  }
`;

const HeroSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 24px 60px;
  position: relative;
  overflow: hidden;

  ${media.lessThan('tablet')} {
    padding: 50px 20px 40px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at center, ${({ $themeColor }) => toRgba(getUserThemeColor($themeColor), 0.1)} 0%, transparent 50%);
    animation: ${shimmer} 15s ease-in-out infinite;
  }

  ${media.lessThan('tablet')} {
    padding: 60px 20px 40px;
  }
`;

const HeroContent = styled.div`
  max-width: ${CONTENT_MINI}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const StoreBranding = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.8s ease-out;
  animation-fill-mode: backwards;
  animation-delay: 0.2s;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const StoreLogoImage = styled.img`
  max-height: 120px;
  max-width: 280px;
  object-fit: contain;

  ${media.lessThan('tablet')} {
    max-height: 80px;
    max-width: 200px;
  }
`;

const StoreName = styled(Typo.ContentBigHeader)`
  color: ${({ $themeColor }) => getUserThemeContrastColor($themeColor)};
  text-shadow: 0 2px 10px ${toRgba(theme.colors.NEU_999, 0.2)};
  font-size: 24px;
  letter-spacing: 1px;

  ${media.lessThan('tablet')} {
    font-size: 18px;
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.8s ease-out;
  animation-fill-mode: backwards;
  animation-delay: 0.4s;
`;

const SectionDivider = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0;
  animation: ${fadeIn} 0.8s ease-out;
  animation-fill-mode: backwards;
  animation-delay: 0.5s;

  &::before,
  &::after {
    content: '';
    flex: 1;
    max-width: 100px;
    height: 2px;
    background: linear-gradient(
      to ${({ $direction }) => $direction || 'right'},
      transparent,
      ${({ $themeColor }) => toRgba(getUserThemeContrastColor($themeColor), 0.4)}
    );
  }
`;

const WelcomeTitle = styled(Typo.SuperHero)`
  color: ${({ $themeColor }) => getUserThemeContrastColor($themeColor)};
  font-size: 56px;
  line-height: 1.1;
  text-align: center;
  margin-bottom: 16px;
  font-weight: 700;
  text-shadow: 0 2px 20px ${toRgba(theme.colors.NEU_999, 0.15)};

  ${media.lessThan('tablet')} {
    font-size: 36px;
  }

  ${media.lessThan('phablet')} {
    font-size: 28px;
  }
`;

const WelcomeSubtitle = styled(Typo.BigText)`
  color: ${({ $themeColor }) => getUserThemeContrastColor($themeColor)};
  opacity: 0.9;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;

  ${media.lessThan('tablet')} {
    font-size: 18px;
    line-height: 1.5;
  }
`;

const GiftCardContainer = styled.div`
  perspective: 1000px;
  margin-bottom: 20px;
  animation: ${giftCardOpen} 1.2s ease-out;
  animation-fill-mode: backwards;
  animation-delay: 0.6s;
`;

const GiftCard = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 32px 48px;
  box-shadow: 0 25px 80px ${toRgba(theme.colors.NEU_999, 0.3)}, 0 10px 30px ${toRgba(theme.colors.NEU_999, 0.2)};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(
      90deg,
      ${({ $themeColor }) => getDarkerThemeColor($themeColor)},
      ${({ $themeColor }) => getUserThemeColor($themeColor)},
      ${({ $themeColor }) => shadeColor($themeColor, 0.6)}
    );
  }

  ${media.lessThan('tablet')} {
    padding: 24px 32px;
  }

  ${media.lessThan('phablet')} {
    padding: 20px 24px;
  }
`;

const GiftCodeLabel = styled(Typo.SmallText)`
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${theme.colors.NEU_600};
  margin-bottom: 8px;
  text-align: center;
`;

const GiftCode = styled(Typo.ContentBigHeader)`
  color: ${({ $themeColor }) => getDarkerThemeColor($themeColor)};
  font-family: monospace;
  letter-spacing: 4px;
  text-align: center;
  word-break: break-all;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  animation: ${fadeIn} 0.8s ease-out;
  animation-fill-mode: backwards;
  animation-delay: 1.2s;
  cursor: pointer;

  ${media.lessThan('tablet')} {
    display: none;
  }
`;

const ScrollArrow = styled.div`
  width: 30px;
  height: 50px;
  border: 2px solid ${({ $themeColor }) => getUserThemeContrastColor($themeColor)};
  border-radius: 20px;
  position: relative;
  opacity: 0.7;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 10px;
    background: ${({ $themeColor }) => getUserThemeContrastColor($themeColor)};
    border-radius: 2px;
    animation: ${fadeInUp} 1.5s ease-in-out infinite;
  }
`;

// ============================================
// COURSE SELECTION SECTION STYLES
// ============================================

const CourseSelectionSection = styled.section`
  background: ${theme.colors.NEU_0};
  padding: 80px 24px;

  ${media.lessThan('tablet')} {
    padding: 60px 20px;
  }
`;

const SectionContent = styled.div`
  max-width: ${CONTENT_STANDARD}px;
  margin: 0 auto;
`;

const SectionTitle = styled(Typo.PageHeader)`
  text-align: center;
  margin-bottom: 16px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SectionSubtitle = styled(Typo.Text)`
  text-align: center;
  color: ${theme.colors.NEU_600};
  max-width: 600px;
  margin: 0 auto 30px;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: 0.1s;
  animation-fill-mode: backwards;
`;

const CategoryFilterContainer = styled.div`
  position: relative;
  margin-bottom: 40px;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: backwards;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    pointer-events: none;
    z-index: 1;
    opacity: ${({ $showFade }) => ($showFade ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, ${theme.colors.NEU_0}, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, ${theme.colors.NEU_0}, transparent);
  }
`;

const CategoryFilterWrapper = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 4px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Center items when they fit */
  justify-content: ${({ $shouldCenter }) => ($shouldCenter ? 'center' : 'flex-start')};
`;

const CategoryChip = styled.button`
  padding: 10px 20px;
  border-radius: 25px;
  border: 2px solid ${({ $isActive, $themeColor }) => ($isActive ? getUserThemeColor($themeColor) : theme.colors.NEU_200)};
  background: ${({ $isActive, $themeColor }) => ($isActive ? getUserThemeColor($themeColor) : theme.colors.NEU_0)};
  color: ${({ $isActive, $themeColor }) => ($isActive ? getUserThemeContrastColor($themeColor) : theme.colors.NEU_PRIMARY)};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ $themeColor }) => getUserThemeColor($themeColor)};
    background: ${({ $isActive, $themeColor }) => ($isActive ? getDarkerThemeColor($themeColor) : shadeColor($themeColor, 0.9))};
  }
`;

const CourseCategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

const CategoryTag = styled.span`
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  background: ${({ $themeColor }) => shadeColor($themeColor, 0.9)};
  color: ${({ $themeColor }) => getDarkerThemeColor($themeColor)};
  font-weight: 500;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;

  .swiper {
    padding: 20px 10px 40px;
    overflow: hidden;
  }

  .swiper-slide {
    height: auto;
  }

  .swiper-button-prev,
  .swiper-button-next {
    color: ${({ $themeColor }) => getDarkerThemeColor($themeColor)};
    background: ${theme.colors.NEU_0};
    width: 50px;
    height: 50px;
    border-radius: 50%;
    box-shadow: 0 4px 20px ${toRgba(theme.colors.NEU_999, 0.15)};
    transition: all 0.3s ease;

    &::after {
      font-size: 18px;
      font-weight: bold;
    }

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 25px ${toRgba(theme.colors.NEU_999, 0.2)};
    }

    ${media.lessThan('tablet')} {
      display: none;
    }
  }

  .swiper-button-prev {
    left: -20px;
  }

  .swiper-button-next {
    right: -20px;
  }
`;

const CourseCardStyled = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px ${toRgba(theme.colors.NEU_999, 0.08)};
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: ${scaleIn} 0.5s ease-out;
  animation-fill-mode: backwards;
  animation-delay: ${({ $index }) => 0.1 + $index * 0.1}s;

  &:hover {
    transform: translateY(-6px) scale(1.01);
    box-shadow: 0 8px 24px ${({ $themeColor }) => toRgba(getUserThemeColor($themeColor), 0.15)}, 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.08)};
  }

  ${({ $isSelected, $themeColor }) =>
    $isSelected &&
    css`
      transform: translateY(-6px) scale(1.01);
      box-shadow: 0 0 0 3px ${getUserThemeColor($themeColor)}, 0 8px 24px ${toRgba(getUserThemeColor($themeColor), 0.2)};
    `}
`;

const CourseImageWrapper = styled.div`
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 60%, ${toRgba(theme.colors.NEU_999, 0.3)} 100%);
    pointer-events: none;
  }
`;

const CourseImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${CourseCardStyled}:hover & {
    transform: scale(1.05);
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, ${theme.colors.NEU_200} 0%, ${theme.colors.NEU_50} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CourseInfo = styled.div`
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CourseTitle = styled(Typo.TextTitle)`
  margin-bottom: 8px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SelectButton = styled(Button)`
  animation: ${({ $isSelected }) => ($isSelected ? 'none' : pulse)} 2s ease-in-out infinite;
  animation-delay: 1s;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      animation: none;
    `}
`;

// ============================================
// CONFIRMATION PANEL STYLES
// ============================================

const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${toRgba(theme.colors.NEU_999, 0.6)};
  backdrop-filter: blur(8px);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ConfirmationPanel = styled.div`
  background: ${theme.colors.NEU_0};
  width: 100%;
  max-width: 800px;
  border-radius: 24px 24px 0 0;
  padding: 40px;
  animation: ${slideUp} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${media.lessThan('tablet')} {
    padding: 30px 20px;
    border-radius: 20px 20px 0 0;
  }
`;

const ConfirmationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
`;

const CloseButton = styled.button`
  background: ${theme.colors.NEU_50};
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 20px;
  color: ${theme.colors.NEU_600};

  &:hover {
    background: ${theme.colors.NEU_200};
    color: ${theme.colors.NEU_PRIMARY};
  }
`;

const SelectedCourseDetails = styled.div`
  display: flex;
  gap: 30px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  ${media.lessThan('tablet')} {
    flex-direction: column;
    gap: 20px;
  }
`;

const SelectedCourseImage = styled.div`
  flex: 0 0 300px;
  border-radius: 12px;
  overflow: hidden;
  align-self: flex-start;

  ${media.lessThan('tablet')} {
    flex: none;
    width: 100%;
    max-height: 200px;
  }
`;

const SelectedCourseInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const SelectedCourseTitle = styled(Typo.ContentBigHeader)`
  margin-bottom: 12px;
  text-align: left;
  flex-shrink: 0;
`;

const SelectedCourseDescriptionWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.NEU_50};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.NEU_300};
    border-radius: 2px;
  }
`;

const SelectedCourseDescription = styled(Typo.Text)`
  color: ${theme.colors.NEU_600};
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;

  ${media.lessThan('phablet')} {
    flex-direction: column;
  }
`;

const ClaimButton = styled(Button)`
  flex: 1;
  padding: 16px 32px;
  font-size: 18px;
  animation: ${pulse} 2s ease-in-out infinite;
  animation-delay: 0.5s;
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.ERROR_RED}15;
  border: 1px solid ${theme.colors.ERROR_RED};
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: ${theme.colors.ERROR_RED};
  font-size: 14px;
  text-align: center;
`;

// ============================================
// FOOTER STYLES
// ============================================

const FooterSection = styled.footer`
  // background: ${theme.colors.NEU_50};
  padding: 40px 24px;
  text-align: center;
`;

const FooterContent = styled.div`
  max-width: ${CONTENT_MINI}px;
  margin: 0 auto;
`;

const PoweredBy = styled(Typo.Info)`
  color: ${theme.colors.NEU_600};
  margin-top: 16px;
`;

// ============================================
// LOADING STATE
// ============================================

const LoadingWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $themeColor }) => themeGradientBg($themeColor)};
`;

// ============================================
// MAIN COMPONENT
// ============================================

const GiftSelectionLayout = ({
  store,
  availableCourses = [],
  categories = [],
  selectedCategory,
  onCategoryChange,
  onSelectCourse,
  onConfirmSelection,
  giftData,
  isLoading,
  error,
}) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const themeColor = store?.theme?.color || store?.themeColor || 'teal';
  const storeName = store?.name || store?.storeName || 'Store';
  const storeLogo = store?.theme?.logoUrl || store?.logoUrl;

  const giftCode = giftData?.code || giftData?.hash || '';
  const senderName = giftData?.senderName || giftData?.from;
  const subtitleText = senderName ? `A special gift from ${senderName}` : 'A special gift awaits you';

  const handleSelectCourse = useCallback(
    (course) => {
      setSelectedCourse(course);
      setShowConfirmation(true);
      if (onSelectCourse) {
        onSelectCourse(course);
      }
    },
    [onSelectCourse],
  );

  const handleConfirm = useCallback(() => {
    if (onConfirmSelection && selectedCourse) {
      onConfirmSelection(selectedCourse);
    }
  }, [onConfirmSelection, selectedCourse]);

  const handleBack = useCallback(() => {
    setShowConfirmation(false);
    setSelectedCourse(null);
  }, []);

  const scrollToSelection = useCallback(() => {
    const section = document.getElementById('course-selection');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  if (isLoading) {
    return (
      <LoadingWrapper $themeColor={themeColor}>
        <Loading themeColor={getUserThemeContrastColor(themeColor)} />
      </LoadingWrapper>
    );
  }

  const getCourseImage = (course) => {
    if (!course) return null;
    const { coverImage, coverUrl } = course;
    if (!coverImage) return coverUrl;
    return Array.isArray(coverImage) ? coverImage[0]?.url : coverImage?.url;
  };

  return (
    <>
      <HeroWrapper $themeColor={themeColor}>
        {/* Logo Section - White background */}
        <LogoSection>
          <LogoWrapper>
            {storeLogo ? (
              <StoreLogoImage src={storeLogo} alt={storeName} />
            ) : (
              <Avatar name={storeName} size='big' bgcolor={getDarkerThemeColor(themeColor)}>
                <Typo.ContentBigHeader color={getUserThemeContrastColor(themeColor)}>{storeName?.charAt(0)?.toUpperCase()}</Typo.ContentBigHeader>
              </Avatar>
            )}
          </LogoWrapper>
        </LogoSection>

        {/* Hero Section - Colored gradient */}
        <HeroSection $themeColor={themeColor}>
          <HeroContent>
            <StoreBranding>
              <StoreName $themeColor={themeColor}>{storeName}</StoreName>
            </StoreBranding>
            <WelcomeMessage>
              <WelcomeTitle $themeColor={themeColor}>You've received a gift!</WelcomeTitle>
              <WelcomeSubtitle $themeColor={themeColor}>{subtitleText}</WelcomeSubtitle>
            </WelcomeMessage>

            {giftCode && (
              <GiftCardContainer>
                <GiftCard $themeColor={themeColor}>
                  <GiftCodeLabel>Your Gift Code</GiftCodeLabel>
                  <GiftCode $themeColor={themeColor}>{giftCode}</GiftCode>
                </GiftCard>
              </GiftCardContainer>
            )}
          </HeroContent>

          <ScrollIndicator onClick={scrollToSelection}>
            <ScrollArrow $themeColor={themeColor} />
          </ScrollIndicator>
        </HeroSection>
      </HeroWrapper>

      {/* Course Selection Section */}
      <CourseSelectionSection id='course-selection'>
        <SectionContent>
          <SectionTitle>Choose Your Free Course</SectionTitle>
          <SectionSubtitle>Select one of the amazing courses below to claim your gift</SectionSubtitle>

          {categories.length > 0 && (
            <CategoryFilterContainer $showFade={categories.length > 5}>
              <CategoryFilterWrapper $shouldCenter={categories.length <= 5}>
                <CategoryChip $isActive={!selectedCategory} $themeColor={themeColor} onClick={() => onCategoryChange?.(null)}>
                  All Courses
                </CategoryChip>
                {categories.map((category) => (
                  <CategoryChip
                    key={category.id}
                    $isActive={selectedCategory === category.id}
                    $themeColor={themeColor}
                    onClick={() => onCategoryChange?.(category.id)}
                  >
                    {category.name}
                  </CategoryChip>
                ))}
              </CategoryFilterWrapper>
            </CategoryFilterContainer>
          )}

          {availableCourses.length > 0 && (
            <CarouselWrapper $themeColor={themeColor}>
              <Swiper
                modules={[Navigation, Keyboard]}
                navigation={true}
                keyboard={{ enabled: true }}
                slidesPerView={1}
                spaceBetween={24}
                breakpoints={{
                  560: {
                    slidesPerView: 2,
                  },
                  1024: {
                    slidesPerView: 3,
                  },
                  1200: {
                    slidesPerView: Math.min(4, availableCourses.length),
                  },
                }}
              >
                {availableCourses.map((course, index) => {
                  const imageUrl = getCourseImage(course);
                  const isSelected = selectedCourse?.id === course.id;

                  return (
                    <SwiperSlide key={course.id || index}>
                      <CourseCardStyled $themeColor={themeColor} $isSelected={isSelected} $index={index} onClick={() => handleSelectCourse(course)}>
                        <CourseImageWrapper>
                          {imageUrl ? (
                            <CourseImage src={imageUrl} alt={course.title || course.name} loading='lazy' />
                          ) : (
                            <ImagePlaceholder>
                              <Typo.Text color={theme.colors.NEU_300}>No image</Typo.Text>
                            </ImagePlaceholder>
                          )}
                        </CourseImageWrapper>

                        <CourseInfo>
                          {course.categories?.length > 0 && (
                            <CourseCategoryTags>
                              {course.categories.slice(0, 2).map((cat) => (
                                <CategoryTag key={cat.id} $themeColor={themeColor}>
                                  {cat.name}
                                </CategoryTag>
                              ))}
                            </CourseCategoryTags>
                          )}
                          <CourseTitle>{course.title || course.name}</CourseTitle>
                          {/* <CourseDescription>{course.description || course.shortDescription || ''}</CourseDescription> */}
                          <SelectButton
                            $variant='themed'
                            $themeColor={themeColor}
                            $isSelected={isSelected}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectCourse(course);
                            }}
                          >
                            {isSelected ? 'Selected' : 'Select This Course'}
                          </SelectButton>
                        </CourseInfo>
                      </CourseCardStyled>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </CarouselWrapper>
          )}

          {availableCourses.length === 0 && (
            <Flex direction='column' align='center' gap='20'>
              <Typo.Text color={theme.colors.NEU_600}>No courses available at the moment.</Typo.Text>
            </Flex>
          )}
        </SectionContent>
      </CourseSelectionSection>

      {/* Confirmation Panel */}
      {showConfirmation && selectedCourse && (
        <ConfirmationOverlay onClick={handleBack}>
          <ConfirmationPanel onClick={(e) => e.stopPropagation()}>
            <ConfirmationHeader>
              <Typo.SectionHeader $textAlign='left'>Confirm Your Selection</Typo.SectionHeader>
              <CloseButton onClick={handleBack} aria-label='Close'>
                &times;
              </CloseButton>
            </ConfirmationHeader>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SelectedCourseDetails>
              <SelectedCourseImage>
                <Figure.Rectangle>
                  {getCourseImage(selectedCourse) ? (
                    <img src={getCourseImage(selectedCourse)} alt={selectedCourse.title || selectedCourse.name} />
                  ) : (
                    <ImagePlaceholder style={{ position: 'absolute', height: '100%' }}>
                      <Typo.Text color={theme.colors.NEU_300}>No image</Typo.Text>
                    </ImagePlaceholder>
                  )}
                </Figure.Rectangle>
              </SelectedCourseImage>

              <SelectedCourseInfo>
                <SelectedCourseTitle>{selectedCourse.title || selectedCourse.name}</SelectedCourseTitle>
                <SelectedCourseDescriptionWrapper>
                  <SelectedCourseDescription>
                    {selectedCourse.description || selectedCourse.shortDescription || 'No description available.'}
                  </SelectedCourseDescription>
                </SelectedCourseDescriptionWrapper>

                <ConfirmationActions>
                  <ClaimButton
                    $variant='customized'
                    $bgcolor={getUserThemeColor(themeColor)}
                    $hoverColor={getDarkerThemeColor(themeColor)}
                    onClick={handleConfirm}
                  >
                    Claim Your Gift
                  </ClaimButton>
                </ConfirmationActions>
              </SelectedCourseInfo>
            </SelectedCourseDetails>
          </ConfirmationPanel>
        </ConfirmationOverlay>
      )}

      {/* Footer */}
      <FooterSection>
        <FooterContent>
          <Flex direction='column' align='center' gap='12'>
            {storeLogo ? (
              <StoreLogoImage src={storeLogo} alt={storeName} style={{ maxHeight: '50px', opacity: 0.8 }} />
            ) : (
              <Typo.TextTitle>{storeName}</Typo.TextTitle>
            )}
            <PoweredBy>Powered by Coursio</PoweredBy>
          </Flex>
        </FooterContent>
      </FooterSection>
    </>
  );
};

export default GiftSelectionLayout;
